import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState
} from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import {
  getBillingErrorStatus,
  getNewBillingSystemError
} from "Hooks/billingSystem/getNewBillingSystemError";

import {
  getProfileDetailRequest,
  updateBillingProfileDetailRequest
} from "Api/billingSystem";

import type { Organization } from "@packages/client";
import type { RootState } from "Store/configureStore";
import type {
  ProfileDetail,
  ProfileDetailCreatableFields
} from "Api/billingSystem/types";
import { createBillingProfileDetail } from "Api/billingSystem/profile/createBillingProfileDetail.request";
import { getUserBillingProfileDetailsRequest } from "Api/billingSystem/profile/getUserBillingProfileDetails.request";
import { selectCurrentUserUUID } from "Reducers/app/selectors";
// Deep import on purpose: the organization barrel re-enters
// Reducers/billingSystem via its thunks, and that cycle leaves this module
// half-evaluated for siblings (standing.ts) that read our selectors at
// module scope.
import { organizationByNameOrIdSelector } from "Reducers/organization/organization/organization.selectors";

export type UpdateBillingProfileDetailThunkPayload = {
  billingProfileId: string;
  payload: Partial<ProfileDetailCreatableFields>;
};

// Thunks
export type CreateBillingProfileDetailThunkPayload = {
  billingAccountId: string;
} & ProfileDetailCreatableFields;

export const createBillingProfileDetailThunk = createAppAsyncThunk(
  "billingSystem/profile/create",
  async (
    payload: CreateBillingProfileDetailThunkPayload,
    { rejectWithValue }
  ) => {
    try {
      const { billingAccountId, ...profileDetail } = payload;
      return await createBillingProfileDetail(billingAccountId, profileDetail);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const getUserBillingProfileDetailsThunk = createAppAsyncThunk(
  "billingSystem/profile/getAll",
  async (
    _: { forceRefetch?: boolean } | undefined,
    { rejectWithValue, getState }
  ) => {
    try {
      const userId = selectCurrentUserUUID(getState());
      return await getUserBillingProfileDetailsRequest(userId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  },
  {
    condition: (arg, { getState }) => {
      // A forced run bypasses the loaded cache — for decisions where a stale
      // list would give a wrong answer (e.g. picking the wizard's entry step).
      if (arg?.forceRefetch) return true;

      const state = getState();
      const { loading, loaded } = state.billingProfiles.getDetailsState;

      // Don't fetch if already loading or already loaded
      if (loading || loaded) {
        return false;
      }
      return true;
    }
  }
);

export const getProfileDetail = createAppAsyncThunk<
  ProfileDetail,
  { billingProfileId: string },
  { rejectValue: { error: string; status?: number } }
>(
  "billingSystem/profiles/getDetail",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      return await getProfileDetailRequest(billingProfileId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong",
        status: getBillingErrorStatus(err)
      });
    }
  }
);

export const updateBillingProfileDetailThunk = createAppAsyncThunk(
  "billingSystem/profile/update",
  async (
    { billingProfileId, payload }: UpdateBillingProfileDetailThunkPayload,
    { rejectWithValue }
  ) => {
    try {
      return await updateBillingProfileDetailRequest(billingProfileId, payload);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

const billingProfilesAdapter = createEntityAdapter({
  selectId: (profile: ProfileDetail) => profile.billing_profile_id
});

// State type
type BillingProfilesState = EntityState<ProfileDetail, string> & {
  detailByProfileIdState: Record<
    string,
    {
      loading: boolean;
      error?: string;
      errorStatus?: number;
    }
  >;
  createState: {
    loading: boolean;
    error?: string;
  };
  updateState: {
    loading: boolean;
    error?: string;
  };
  getDetailsState: {
    loading: boolean;
    loaded: boolean;
    error?: string;
  };
};

const initialState: BillingProfilesState =
  billingProfilesAdapter.getInitialState({
    detailByProfileIdState: {},
    createState: { loading: false },
    updateState: { loading: false },
    getDetailsState: { loading: false, loaded: false }
  });

// Slice
const billingProfilesSlice = createSlice({
  name: "billingProfiles",
  initialState,
  reducers: {
    clearUpdateError(state) {
      state.updateState.error = undefined;
    },
    clearCreateError(state) {
      state.createState.error = undefined;
    }
  },
  extraReducers: builder => {
    // Create billing profile details by account id
    builder
      .addCase(createBillingProfileDetailThunk.pending, state => {
        setDeep(state, ["createState", "loading"], true);
        setDeep(state, ["createState", "error"], undefined);
      })
      .addCase(createBillingProfileDetailThunk.fulfilled, (state, action) => {
        setDeep(state, ["createState", "loading"], false);
        billingProfilesAdapter.setOne(state, action.payload);
      })
      .addCase(createBillingProfileDetailThunk.rejected, (state, action) => {
        setDeep(state, ["createState", "loading"], false);
        setDeep(state, ["createState", "error"], action.payload?.error);
      });

    // Update billing profile detail
    builder
      .addCase(updateBillingProfileDetailThunk.pending, state => {
        setDeep(state, ["updateState", "loading"], true);
        setDeep(state, ["updateState", "error"], undefined);
      })
      .addCase(updateBillingProfileDetailThunk.fulfilled, (state, action) => {
        setDeep(state, ["updateState", "loading"], false);
        billingProfilesAdapter.setOne(state, action.payload);
      })
      .addCase(updateBillingProfileDetailThunk.rejected, (state, action) => {
        setDeep(state, ["updateState", "loading"], false);
        setDeep(state, ["updateState", "error"], action.payload?.error);
      });

    // Get all billing profile details by user id
    builder
      .addCase(getUserBillingProfileDetailsThunk.pending, state => {
        setDeep(state, ["getDetailsState", "loading"], true);
        setDeep(state, ["getDetailsState", "error"], undefined);
      })
      .addCase(getUserBillingProfileDetailsThunk.fulfilled, (state, action) => {
        setDeep(state, ["getDetailsState", "loading"], false);
        setDeep(state, ["getDetailsState", "loaded"], true);
        billingProfilesAdapter.setAll(state, action.payload.items);
      })
      .addCase(getUserBillingProfileDetailsThunk.rejected, (state, action) => {
        setDeep(state, ["getDetailsState", "loading"], false);
        setDeep(state, ["getDetailsState", "error"], action.payload?.error);
      });

    // Get profile detail
    builder
      .addCase(getProfileDetail.pending, (state, { meta }) => {
        setDeep(
          state,
          ["detailByProfileIdState", meta.arg.billingProfileId, "loading"],
          true
        );
        setDeep(
          state,
          ["detailByProfileIdState", meta.arg.billingProfileId, "error"],
          undefined
        );
        setDeep(
          state,
          ["detailByProfileIdState", meta.arg.billingProfileId, "errorStatus"],
          undefined
        );
      })
      .addCase(getProfileDetail.fulfilled, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(
          state,
          ["detailByProfileIdState", billingProfileId, "loading"],
          false
        );
        billingProfilesAdapter.setOne(state, action.payload);
      })
      .addCase(getProfileDetail.rejected, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(
          state,
          ["detailByProfileIdState", billingProfileId, "loading"],
          false
        );
        setDeep(
          state,
          ["detailByProfileIdState", billingProfileId, "error"],
          action.payload?.error
        );
        setDeep(
          state,
          ["detailByProfileIdState", billingProfileId, "errorStatus"],
          action.payload?.status
        );
      });
  }
});

export const { clearUpdateError, clearCreateError } =
  billingProfilesSlice.actions;
export default billingProfilesSlice.reducer;

// Selectors
const billingProfilesStateSelector = (state: RootState) =>
  state.billingProfiles;

const selectors = billingProfilesAdapter.getSelectors(
  billingProfilesStateSelector
);

// Profile detail
export const selectProfileDetail = selectors.selectById;

export const selectProfileDetailLoading = createSelector(
  billingProfilesStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.detailByProfileIdState[billingProfileId]?.loading ?? false
);

export const selectProfileDetailError = createSelector(
  billingProfilesStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.detailByProfileIdState[billingProfileId]?.error
);

export const selectProfileDetailErrorStatus = createSelector(
  billingProfilesStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.detailByProfileIdState[billingProfileId]?.errorStatus
);

export const selectBillingProfileDetailCreateLoading = createSelector(
  billingProfilesStateSelector,
  state => !!state.createState.loading
);

export const selectBillingProfileDetailCreateError = createSelector(
  billingProfilesStateSelector,
  state => state.createState.error
);

export const selectBillingProfileDetailUpdateLoading = createSelector(
  billingProfilesStateSelector,
  state => !!state.updateState.loading
);

export const selectBillingProfileDetailUpdateError = createSelector(
  billingProfilesStateSelector,
  state => state.updateState.error
);

export const selectUserBillingProfileDetails = selectors.selectAll;

export const selectUserBillingProfileDetailsByAccountId = createSelector(
  selectUserBillingProfileDetails,
  (_: RootState, billingAccountId?: string) => billingAccountId,
  (details, id) =>
    id ? details.filter(detail => detail.billing_account_id === id) : details
);

export const selectCurrentBillingProfile = createSelector(
  (state: RootState) => state.app.routeHistory.params["*"]?.split("/")[1],
  selectUserBillingProfileDetails,
  (id, profiles) => profiles.find(profile => profile.billing_profile_id === id)
);

/**
 * An organization can only be linked to a billing profile of the same pricing
 * kind: a fixed organization to a fixed profile, a flexible one to a flex
 * profile. Organizations expose this as `type` ("flexible" | "fixed") while
 * billing profiles use `pricing_model` ("flex" | "fixed"), so we normalise the
 * organization side before comparing.
 */
export const organizationPricingModel = (
  organization: Organization
): ProfileDetail["pricing_model"] =>
  organization.type === "fixed" ? "fixed" : "flex";

/**
 * Splits a billing account's profiles by whether they can be linked to the
 * given organization: a profile is eligible when its pricing_model matches
 * the organization's type (flex ↔ flexible, fixed ↔ fixed). While the
 * organization isn't resolved yet, every profile stays eligible so a slow
 * fetch never blocks the selection.
 */
export const selectBillingProfilesByEligibility = createSelector(
  [
    (state: RootState, params: { billingAccountId?: string }) =>
      selectUserBillingProfileDetailsByAccountId(
        state,
        params.billingAccountId
      ),
    (state: RootState, params: { organizationNameOrId?: string }) =>
      params.organizationNameOrId
        ? organizationByNameOrIdSelector(state, {
            organizationNameOrId: params.organizationNameOrId
          })
        : undefined
  ],
  (profiles, organization) => {
    if (!organization) {
      return {
        eligible: profiles,
        ineligible: [] as ProfileDetail[],
        organizationPricingModel: undefined
      };
    }

    const pricingModel = organizationPricingModel(organization);
    return {
      eligible: profiles.filter(
        profile => profile.pricing_model === pricingModel
      ),
      ineligible: profiles.filter(
        profile => profile.pricing_model !== pricingModel
      ),
      organizationPricingModel: pricingModel
    };
  }
);

export const selectUserBillingProfileDetailsLoaded = createSelector(
  billingProfilesStateSelector,
  state => state.getDetailsState.loaded
);

export const selectUserBillingProfileDetailsLoading = createSelector(
  billingProfilesStateSelector,
  state => state.getDetailsState.loading
);

export const selectUserBillingProfileDetailsError = createSelector(
  billingProfilesStateSelector,
  state => state.getDetailsState.error
);
